import type { ChronologyValidationStatus } from './chronology.js';
import type { PalaceId } from './domain.js';
import {
  inspectSavedChartValue,
  type LocalChartRepository,
  type SavedChartRecord,
} from './localPersistence.js';

export const LOCAL_CHART_TRANSFER_SCHEMA_VERSION = '1' as const;
export const MAX_LOCAL_CHART_BACKUP_RECORDS = 100;

export interface LocalChartPrivateBackupFile {
  schemaVersion: '1';
  kind: 'ziwei_local_chart_private_backup';
  exportedAt: string;
  records: readonly SavedChartRecord[];
  disclosure: string;
}

export interface RedactedPalaceExport {
  id: PalaceId;
  label: string;
  isBodyPalace: boolean;
  majorStars: readonly string[];
  minorStars: readonly string[];
}

export interface RedactedChartExport {
  zodiac: string;
  fiveElementsClass: string;
  ruleset: string;
  chronology?: string;
  chronologyValidationStatus?: ChronologyValidationStatus;
  palaces: readonly RedactedPalaceExport[];
}

export interface LocalChartRedactedShareFile {
  schemaVersion: '1';
  kind: 'ziwei_local_chart_redacted_share';
  exportedAt: string;
  charts: readonly RedactedChartExport[];
  disclosure: string;
}

export interface LocalChartRestoreResult {
  importedCount: number;
  resultingCount: number;
  disclosure: string;
}


export type LocalChartTransferInspection =
  | Readonly<{ status: 'valid_private_backup'; kind: 'ziwei_local_chart_private_backup'; restorable: true; containsSensitiveBirthData: true; itemCount: number; exportedAt: string; disclosure: string }>
  | Readonly<{ status: 'valid_redacted_share'; kind: 'ziwei_local_chart_redacted_share'; restorable: false; containsSensitiveBirthData: false; itemCount: number; exportedAt: string; disclosure: string }>
  | Readonly<{ status: 'unsupported_version'; schemaVersion: string; restorable: false; containsSensitiveBirthData: boolean; disclosure: string }>
  | Readonly<{ status: 'invalid'; restorable: false; containsSensitiveBirthData: boolean; disclosure: string }>;

function assertExportedAt(value: string): void {
  if (!value.trim()) throw new Error('Local chart transfer exportedAt must be a non-empty string');
}

function repositoryRecords(repository: LocalChartRepository): readonly SavedChartRecord[] {
  return Object.freeze(repository.list().map((summary) => {
    const record = repository.get(summary.id);
    if (!record) throw new Error(`Local chart repository record ${summary.id} became unavailable during export`);
    return record;
  }));
}

export function buildLocalChartPrivateBackup(
  repository: LocalChartRepository,
  exportedAt: string,
): LocalChartPrivateBackupFile {
  assertExportedAt(exportedAt);
  const records = repositoryRecords(repository);
  if (records.length > MAX_LOCAL_CHART_BACKUP_RECORDS) throw new Error(`Local chart backup exceeds ${MAX_LOCAL_CHART_BACKUP_RECORDS} records`);
  return Object.freeze({
    schemaVersion: LOCAL_CHART_TRANSFER_SCHEMA_VERSION,
    kind: 'ziwei_local_chart_private_backup',
    exportedAt,
    records: Object.freeze(records.map((record) => Object.freeze(structuredClone(record)))),
    disclosure: '此私密備份包含完整出生資料與命盤內容，只應保存於你信任的裝置或儲存空間；匯出與匯入都在瀏覽器本機處理，不需要上傳伺服器。',
  });
}

export function buildLocalChartRedactedShare(
  repository: LocalChartRepository,
  exportedAt: string,
): LocalChartRedactedShareFile {
  assertExportedAt(exportedAt);
  const charts = repositoryRecords(repository).map((record): RedactedChartExport => Object.freeze({
    zodiac: record.chart.zodiac,
    fiveElementsClass: record.chart.fiveElementsClass,
    ruleset: `${record.chart.calculation.rulesetId}@${record.chart.calculation.rulesetVersion}`,
    ...(record.chronology ? {
      chronology: `${record.chronology.providerId}@${record.chronology.providerVersion}`,
      chronologyValidationStatus: record.chronology.validationStatus,
    } : {}),
    palaces: Object.freeze(record.chart.palaceOrder.map((palaceId) => {
      const palace = record.chart.palaces[palaceId];
      return Object.freeze({
        id: palace.id,
        label: palace.label,
        isBodyPalace: palace.isBodyPalace,
        majorStars: Object.freeze(palace.majorStars.map((star) => star.label)),
        minorStars: Object.freeze(palace.minorStars.map((star) => star.label)),
      });
    })),
  }));
  return Object.freeze({
    schemaVersion: LOCAL_CHART_TRANSFER_SCHEMA_VERSION,
    kind: 'ziwei_local_chart_redacted_share',
    exportedAt,
    charts: Object.freeze(charts),
    disclosure: '此隱私分享版刻意移除出生日期、時辰、性別、別名與命盤 fingerprint，只保留命盤摘要與版本來源；它不是完整備份，因此不能用來還原本機命盤資料。',
  });
}

function parseTransferInput(input: string | unknown): unknown {
  if (typeof input !== 'string') return input;
  try {
    return JSON.parse(input) as unknown;
  } catch {
    throw new Error('Local chart backup is not valid JSON');
  }
}

function validatePrivateBackup(input: string | unknown): LocalChartPrivateBackupFile {
  const value = parseTransferInput(input);
  if (!value || typeof value !== 'object') throw new Error('Local chart backup has invalid shape');
  const object = value as Record<string, unknown>;
  if (object.schemaVersion !== LOCAL_CHART_TRANSFER_SCHEMA_VERSION) throw new Error(`Unsupported local chart transfer schema ${String(object.schemaVersion)}`);
  if (object.kind !== 'ziwei_local_chart_private_backup') throw new Error('Only private backup files can be restored');
  if (typeof object.exportedAt !== 'string' || !object.exportedAt.trim()) throw new Error('Local chart backup exportedAt is invalid');
  if (!Array.isArray(object.records)) throw new Error('Local chart backup records must be an array');
  if (object.records.length > MAX_LOCAL_CHART_BACKUP_RECORDS) throw new Error(`Local chart backup exceeds ${MAX_LOCAL_CHART_BACKUP_RECORDS} records`);
  const seen = new Set<string>();
  const records: SavedChartRecord[] = [];
  for (const candidate of object.records) {
    const inspection = inspectSavedChartValue(JSON.stringify(candidate));
    if (inspection.status !== 'valid') throw new Error(`Local chart backup contains invalid record: ${inspection.status}`);
    if (seen.has(inspection.record.id)) throw new Error(`Local chart backup contains duplicate record ${inspection.record.id}`);
    seen.add(inspection.record.id);
    records.push(inspection.record);
  }
  return {
    schemaVersion: LOCAL_CHART_TRANSFER_SCHEMA_VERSION,
    kind: 'ziwei_local_chart_private_backup',
    exportedAt: object.exportedAt,
    records,
    disclosure: typeof object.disclosure === 'string' ? object.disclosure : '',
  };
}



function isPlainObject(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function hasOnlyKeys(object: Record<string, unknown>, allowed: readonly string[]): boolean {
  return Object.keys(object).every((key) => allowed.includes(key));
}

function validRedactedShare(value: unknown): value is LocalChartRedactedShareFile {
  if (!isPlainObject(value) || !hasOnlyKeys(value, ['schemaVersion', 'kind', 'exportedAt', 'charts', 'disclosure'])) return false;
  if (value.schemaVersion !== LOCAL_CHART_TRANSFER_SCHEMA_VERSION || value.kind !== 'ziwei_local_chart_redacted_share' || typeof value.exportedAt !== 'string' || !value.exportedAt.trim() || !Array.isArray(value.charts)) return false;
  if (value.charts.length > MAX_LOCAL_CHART_BACKUP_RECORDS) return false;
  return value.charts.every((candidate) => {
    if (!isPlainObject(candidate) || !hasOnlyKeys(candidate, ['zodiac', 'fiveElementsClass', 'ruleset', 'chronology', 'chronologyValidationStatus', 'palaces'])) return false;
    if (typeof candidate.zodiac !== 'string' || typeof candidate.fiveElementsClass !== 'string' || typeof candidate.ruleset !== 'string' || !Array.isArray(candidate.palaces) || candidate.palaces.length !== 12) return false;
    if (candidate.chronology !== undefined && typeof candidate.chronology !== 'string') return false;
    if (candidate.chronologyValidationStatus !== undefined && !['verified_range', 'experimental', 'unavailable'].includes(String(candidate.chronologyValidationStatus))) return false;
    return candidate.palaces.every((palace) => isPlainObject(palace)
      && hasOnlyKeys(palace, ['id', 'label', 'isBodyPalace', 'majorStars', 'minorStars'])
      && typeof palace.id === 'string' && typeof palace.label === 'string' && typeof palace.isBodyPalace === 'boolean'
      && Array.isArray(palace.majorStars) && palace.majorStars.every((star) => typeof star === 'string')
      && Array.isArray(palace.minorStars) && palace.minorStars.every((star) => typeof star === 'string'));
  });
}

export function inspectLocalChartTransfer(input: string | unknown): LocalChartTransferInspection {
  let value: unknown;
  try {
    value = parseTransferInput(input);
  } catch {
    return Object.freeze({ status: 'invalid', restorable: false, containsSensitiveBirthData: false, disclosure: '檔案不是有效 JSON，未寫入任何本機資料。' });
  }
  if (!isPlainObject(value)) return Object.freeze({ status: 'invalid', restorable: false, containsSensitiveBirthData: false, disclosure: '檔案結構無效，未寫入任何本機資料。' });
  if (value.schemaVersion !== LOCAL_CHART_TRANSFER_SCHEMA_VERSION) {
    return Object.freeze({
      status: 'unsupported_version',
      schemaVersion: typeof value.schemaVersion === 'string' ? value.schemaVersion : 'unknown',
      restorable: false,
      containsSensitiveBirthData: value.kind === 'ziwei_local_chart_private_backup',
      disclosure: '此匯出格式版本目前不支援；系統不會嘗試自動轉換或寫入。',
    });
  }
  if (value.kind === 'ziwei_local_chart_private_backup') {
    try {
      const backup = validatePrivateBackup(value);
      return Object.freeze({
        status: 'valid_private_backup', kind: backup.kind, restorable: true, containsSensitiveBirthData: true,
        itemCount: backup.records.length, exportedAt: backup.exportedAt,
        disclosure: '這是可還原的私密備份，內含完整出生資料與命盤內容；確認來源可信後再匯入。',
      });
    } catch {
      return Object.freeze({ status: 'invalid', restorable: false, containsSensitiveBirthData: true, disclosure: '私密備份內容驗證失敗；未寫入任何本機資料。' });
    }
  }
  if (value.kind === 'ziwei_local_chart_redacted_share') {
    if (!validRedactedShare(value)) return Object.freeze({ status: 'invalid', restorable: false, containsSensitiveBirthData: false, disclosure: '隱私分享版結構驗證失敗；此檔案不可還原。' });
    return Object.freeze({
      status: 'valid_redacted_share', kind: value.kind, restorable: false, containsSensitiveBirthData: false,
      itemCount: value.charts.length, exportedAt: value.exportedAt,
      disclosure: '這是隱私分享版，只供查看命盤摘要，刻意不可匯入還原。',
    });
  }
  return Object.freeze({ status: 'invalid', restorable: false, containsSensitiveBirthData: false, disclosure: '不支援的檔案類型；未寫入任何本機資料。' });
}

export function restoreLocalChartPrivateBackup(
  repository: LocalChartRepository,
  input: string | unknown,
): LocalChartRestoreResult {
  const backup = validatePrivateBackup(input);
  // Validation is intentionally completed before the first repository write.
  for (const record of [...backup.records].sort((a, b) => a.savedAt.localeCompare(b.savedAt) || a.id.localeCompare(b.id))) {
    repository.save(record);
  }
  return Object.freeze({
    importedCount: backup.records.length,
    resultingCount: repository.list().length,
    disclosure: '私密備份已在本機驗證後合併；相同命盤 ID 會以備份內容更新，並仍受本機保存筆數上限約束。沒有資料被傳送到網路。',
  });
}

/**
 * Returns schema-validated private-backup records without writing to storage.
 * Production import flows should still run domain integrity gates before restore.
 */
export function validatedLocalChartPrivateBackupRecords(input: string | unknown): readonly SavedChartRecord[] {
  const backup = validatePrivateBackup(input);
  return Object.freeze(backup.records.map((record) => Object.freeze(structuredClone(record))));
}
